"use client";

/**
 * /password-recovery — same cream/burgundy/coral theme as the new /login
 * page (mock 19-desktop-login). Two-column split on lg+ (visual panel
 * left, form right), single-column on mobile.
 *
 * Existing Redux flow preserved:
 *  - `requestPasswordReset({ email, language })` on submit
 *  - 30-second cooldown to throttle re-tries
 *  - inline error / loading display backed by `state.auth.{error,loading}`
 */

import React, { FC, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { Route } from "next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { requestPasswordReset } from "@/app/lib/reducers/authReducer";
import AuthVisualPanel from "@/components/AuthVisualPanel";

// Rendue cote serveur (pas de `ssr: false`) : le gabarit de 220px qui la
// remplacait le temps du chargement fait moins de la moitie de la hauteur
// reelle du bloc — tout ce qui suit sautait a l'hydratation. Le composant
// n'utilise rien du navigateur au rendu. Le `dynamic` reste : il garde le
// chunk hors du bundle initial.
const SectionNewsletter = dynamic(
  () =>
    import(
      "@/app/[lang]/[currency]/(chrome)/(home)/desktop/SectionNewsletter"
    )
);

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const PagePasswordRecovery: FC = () => {
  const [email, setEmail] = useState("");
  const [isCooldown, setIsCooldown] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");

  const selectedLanguage = useAppSelector(
    (s: any) => s?.language?.selectedLanguage || "en_US"
  );
  const { loading, error } = useAppSelector((s: any) => s.auth);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !EMAIL_RE.test(email)) {
      dispatch({
        type: "RESET_LOST_PASSWORD_FAILURE",
        error: t("please_enter_valid_email_address"),
      });
      return;
    }
    setIsCooldown(true);
    setTimeout(() => setIsCooldown(false), 30000);
    setSubmitted(true);
    dispatch(requestPasswordReset({ email, language: selectedLanguage }));
  };

  const renderFormCard = () => (
    <div
      className="gdx-card"
      style={{
        background: "white",
        padding: "32px 28px",
        borderColor: "transparent",
        boxShadow: "var(--g-shadow-md, 0 6px 24px rgba(0,0,0,.06))",
        width: "100%",
        maxWidth: 460,
        margin: "0 auto",
      }}
    >
      <h1
        style={{
          fontSize: "clamp(24px, 4vw, 30px)",
          color: "var(--g-burgundy)",
          fontWeight: 800,
          marginBottom: 6,
          lineHeight: 1.15,
        }}
      >
        {t("password_recovery", { defaultValue: "Mot de passe oublié ?" })}
      </h1>
      <p
        style={{
          fontSize: 14,
          color: "var(--g-ink-500)",
          marginBottom: 22,
        }}
      >
        {t("enter_email_for_reset", {
          defaultValue:
            "Saisissez l'adresse email de votre compte. Nous vous envoyons un lien pour réinitialiser votre mot de passe.",
        })}
      </p>

      {submitted && !error && !loading && (
        <div
          role="status"
          style={{
            padding: 16,
            borderRadius: 12,
            background: "rgba(31,122,69,.08)",
            border: "1px solid rgba(31,122,69,.25)",
            marginBottom: 16,
            color: "var(--g-ink-700)",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <strong
            style={{ color: "var(--g-green-ada, #1F7A45)", display: "block" }}
          >
            {t("password_recovery_email_sent_title", {
              defaultValue: "Lien envoyé !",
            })}
          </strong>
          {t("password_recovery_email_sent_body", {
            defaultValue:
              "Vérifiez votre boîte (et le dossier spam). Le lien expire dans 30 minutes.",
          })}
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            borderRadius: 10,
            background: "rgba(224,99,90,.10)",
            border: "1px solid rgba(224,99,90,.25)",
            color: "var(--g-coral)",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {/request failed|status code|network|\b40\d\b|\b50\d\b|err_/i.test(
            String(error)
          )
            ? t("password_recovery_error", {
                defaultValue:
                  "Impossible d'envoyer le lien. Vérifiez votre adresse e-mail et réessayez.",
              })
            : String(error)}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <div>
          <div className="gdx-label" style={{ marginBottom: 6 }}>
            {t("email_address", { defaultValue: "Adresse email" })}
          </div>
          <input
            type="email"
            required
            autoComplete="email"
            className="gdx-input"
            placeholder="vous@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={loading || isCooldown}
          style={{
            height: 50,
            marginTop: 6,
            width: "100%",
            fontWeight: 700,
            fontSize: 14,
            background:
              loading || isCooldown
                ? "var(--g-ink-300, #cdb6a3)"
                : "var(--g-green-ada, #1F7A45)",
            color: "white",
            border: 0,
            borderRadius: 999,
            cursor: loading || isCooldown ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
          }}
        >
          {loading
            ? t("sending", { defaultValue: "Envoi en cours…" })
            : isCooldown
              ? t("wait_before_retrying", {
                  defaultValue: "Patientez avant de réessayer",
                })
              : t("send_reset_link", {
                  defaultValue: "Envoyer le lien de réinitialisation",
                })}
        </button>
      </form>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          margin: "22px 0 18px",
        }}
        aria-hidden="true"
      >
        <span style={{ flex: 1, height: 1, background: "var(--g-line)" }} />
        <span
          style={{
            fontSize: 11,
            color: "var(--g-ink-500)",
            textTransform: "uppercase",
            letterSpacing: ".08em",
            fontWeight: 600,
          }}
        >
          {t("login_or", { defaultValue: "ou" })}
        </span>
        <span style={{ flex: 1, height: 1, background: "var(--g-line)" }} />
      </div>

      <p
        style={{
          fontSize: 13,
          color: "var(--g-ink-500)",
          textAlign: "center",
        }}
      >
        {t("remember_password", { defaultValue: "Vous vous souvenez ?" })}{" "}
        <Link
          href={"/login" as Route}
          style={{
            color: "var(--g-coral)",
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          {t("login_here", { defaultValue: "Se connecter" })}
        </Link>
      </p>

      <div
        className="flex items-center justify-center"
        style={{
          gap: 18,
          marginTop: 26,
          fontSize: 11,
          color: "var(--g-ink-500)",
          flexWrap: "wrap",
        }}
      >
        <span className="inline-flex items-center" style={{ gap: 4 }}>
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
          </svg>
          {t("login_trust_secure", { defaultValue: "Paiement sécurisé" })}
        </span>
        <span>{t("login_trust_rgpd", { defaultValue: "RGPD" })}</span>
        <span>{t("login_trust_support", { defaultValue: "Support 7j/7" })}</span>
      </div>
    </div>
  );

  return (
    <div
      className="nc-PagePasswordRecovery"
      style={{ background: "var(--g-cream)" }}
    >
      <div
        className="grid grid-cols-1 lg:grid-cols-2"
        style={{ minHeight: "calc(100vh - 80px)" }}
      >
        <AuthVisualPanel />

        <div
          className="flex items-center justify-center"
          style={{ padding: "40px 16px" }}
        >
          {renderFormCard()}
        </div>
      </div>

      <SectionNewsletter />
    </div>
  );
};

export default PagePasswordRecovery;
